import React, { useMemo, useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useScheduleRoles } from '@/hooks/useScheduleRoles';
import ScheduleRoleField from '@/components/schedules/ScheduleRoleField';
import ScheduleFormProgress, { ScheduleDraftSummary } from '@/components/schedules/ScheduleFormProgress';
import { useSchedulePresenceConfirmation } from '@/hooks/useSchedulePresenceConfirmation';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useGroupLeaderPermission } from '@/components/hooks/useGroupLeaderPermission';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import { motion } from 'framer-motion';
import { Calendar, Plus, Edit, Trash2, Clock, MapPin, Music, Users as UsersIcon, Eye, ExternalLink, CheckCircle, Circle, MessageCircle, Download, CalendarDays, Youtube, Music2, BellRing, Filter, SlidersHorizontal, ChevronDown } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import CalendarView from '@/components/worship/CalendarView';
import { useSearchParams } from 'react-router-dom';
import { downloadICSFile } from '@/utils/icsDownload';
import { buildICSFromSchedule, buildNativeCalendarEventFromSchedule } from '@/utils/icsBuilder';
import { importEventsToNativeCalendar, isNativeIOS } from '@/utils/nativeAndroidFiles';
import { getMemberByUser } from '@/lib/memberLookup';
import { getWorshipTeamMemberByUser, isSameTeamMember } from '@/lib/teamMemberLookup';
import { deliverMemberNotification } from '@/lib/notificationDelivery';
import { notifyTeamLeaders } from '@/lib/teamLeaderNotifications';
import { resolveScheduleRecipient } from '@/lib/scheduleRecipientResolver';
import { fetchGlobalAvailabilityData, findGlobalAvailabilityBlock } from '@/lib/globalAvailability';
import { buildCollectiveScheduleWhatsAppMessage, buildWhatsAppShareUrl } from '@/lib/whatsappScheduleMessage';
import { isScheduleConfirmationClosed } from '@/lib/rankingMBV';
import {
  REQUIRED_SCHEDULE_TIME_MESSAGE,
  UNAVAILABLE_MEMBER_MESSAGE,
  findScheduleMemberConflict,
  hasRequiredScheduleTimes,
  validateRequiredScheduleTimes
} from '@/lib/scheduleConflicts';

const FORM_STEPS = ['Dados', 'Integrantes', 'Hinos', 'Revisão'];

export default function EscalasLouvor() {
  const [showForm, setShowForm] = useState(false);
  const [activeFormStep, setActiveFormStep] = useState(1);
  const formDialogRef = React.useRef(null);
  const roleCatalog = useScheduleRoles('worship');
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [viewingSchedule, setViewingSchedule] = useState(null);
  const [viewingSongLyrics, setViewingSongLyrics] = useState(null);
  const [whatsappModalSchedule, setWhatsappModalSchedule] = useState(null);
  const [showCalendarView, setShowCalendarView] = useState(false);
  const [quickFilter, setQuickFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('detailed');
  const [showViewModePanel, setShowViewModePanel] = useState(false);
  const defaultQuickFilterAppliedRef = React.useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    start_time: '',
    end_time: '',
    location: '',
    audiences: [],
    rehearsal_time: '',
    observations: '',
    songs: [],
    members: []
  });

  const queryClient = useQueryClient();
  const confirmPresenceMutation = useSchedulePresenceConfirmation({
    entityName: 'WorshipSchedule',
    queryKey: 'worshipSchedules',
    setViewingSchedule,
  });
  const isValidDateValue = (value) => value instanceof Date && !Number.isNaN(value.getTime());
  const parseScheduleDate = (dateStr) => {
    if (!dateStr) return null;
    const normalized = String(dateStr).slice(0, 10);
    const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) {
      const parsedDate = new Date(dateStr);
      return isValidDateValue(parsedDate) ? parsedDate : null;
    }
    const [, year, month, day] = match;
    const parsedDate = new Date(Number(year), Number(month) - 1, Number(day));
    return isValidDateValue(parsedDate) ? parsedDate : null;
  };
  const getScheduleTimestamp = (dateStr, fallback = Number.POSITIVE_INFINITY) => {
    const parsedDate = parseScheduleDate(dateStr);
    return isValidDateValue(parsedDate) ? parsedDate.getTime() : fallback;
  };
  const formatScheduleDateLabel = (dateStr, pattern, fallback = 'Data indisponivel') => {
    const parsedDate = parseScheduleDate(dateStr);
    return isValidDateValue(parsedDate) ? format(parsedDate, pattern, { locale: ptBR }) : fallback;
  };
  const formatDateTimeLabel = (dateStr, pattern, fallback = 'Data indisponivel') => {
    const parsedDate = new Date(dateStr);
    return isValidDateValue(parsedDate) ? format(parsedDate, pattern) : fallback;
  };

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me()
  });

  const { data: permissions } = useQuery({
    queryKey: ['adminPermissions', user?.email],
    queryFn: async () => {
      if (!user?.email) return null;
      const byUserEmail = await base44.entities.AdminPermission.filter({ user_email: user.email });
      if (byUserEmail.length > 0) return byUserEmail[0];
      const byEmail = await base44.entities.AdminPermission.filter({ email: user.email });
      return byEmail[0] || null;
    },
    enabled: !!user?.email
  });

  const { data: schedules = [], isLoading } = useQuery({
    queryKey: ['worshipSchedules'],
    queryFn: () => base44.entities.WorshipSchedule.list('-date')
  });
  const { data: mediaSchedules = [] } = useQuery({
    queryKey: ['mediaSchedules'],
    queryFn: () => base44.entities.MediaSchedule.list('-date')
  });
  const { data: kidsSchedules = [] } = useQuery({
    queryKey: ['kidsSchedules'],
    queryFn: () => base44.entities.KidsMBVSchedule.list('-date')
  });
  const { data: receptionSchedules = [] } = useQuery({
    queryKey: ['receptionSchedules'],
    queryFn: () => base44.entities.ReceptionSchedule.list('-date')
  });
  const { data: danceSchedules = [] } = useQuery({
    queryKey: ['danceSchedules'],
    queryFn: () => base44.entities.DanceSchedule.list('-date')
  });

  React.useEffect(() => {
    const scheduleId = searchParams.get('scheduleId');
    if (!scheduleId || schedules.length === 0) return;
    const targetSchedule = schedules.find((s) => s.id === scheduleId);
    if (targetSchedule) {
      setViewingSchedule(targetSchedule);
    }
  }, [searchParams, schedules]);

  const closeViewingSchedule = () => {
    setViewingSchedule(null);
    if (searchParams.get('scheduleId')) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('scheduleId');
      setSearchParams(nextParams, { replace: true });
    }
  };

  const { canManageWorship } = useGroupLeaderPermission();
  const isAdmin = user?.role === 'admin' || permissions?.is_super_admin;
  const canViewAllSchedules = isAdmin || canManageWorship;
  const canSendScheduleNotifications = canManageWorship || isAdmin;

  React.useEffect(() => {
    if (!user || isLoading || defaultQuickFilterAppliedRef.current) return;
    setQuickFilter(canViewAllSchedules ? 'all' : 'mine');
    defaultQuickFilterAppliedRef.current = true;
  }, [user, isLoading, canViewAllSchedules]);

  const { data: songs = [] } = useQuery({
    queryKey: ['songs'],
    queryFn: () => base44.entities.Song.list()
  });

  const { data: members = [] } = useQuery({
    queryKey: ['worshipMembers'],
    queryFn: () => base44.entities.WorshipTeamMember.list()
  });

  const { data: churchMembers = [] } = useQuery({
    queryKey: ['churchMembers'],
    queryFn: () => base44.entities.Member.filter({ status: 'Ativo' })
  });

  const { data: leaders = [] } = useQuery({
    queryKey: ['leaders'],
    queryFn: () => base44.entities.Leader.filter({ is_active: true })
  });

  const { data: globalAvailabilityData } = useQuery({
    queryKey: ['globalAvailabilityData'],
    queryFn: fetchGlobalAvailabilityData
  });
  const availabilityBlocks = globalAvailabilityData?.blocksByTeam?.worship || [];
  const getAvailabilityBlockForMember = (member, date) =>
    findGlobalAvailabilityBlock(member, date, globalAvailabilityData);

  const churchMemberPhotoById = useMemo(
    () => new Map(churchMembers.map((m) => [m.id, m.photo_url])),
    [churchMembers]
  );

  const scheduleMemberPhotoById = useMemo(
    () =>
      new Map(
        members.map((m) => [
          m.id,
          (m.member_id && churchMemberPhotoById.get(m.member_id)) || m.photo_url || ''
        ])
      ),
    [members, churchMemberPhotoById]
  );

  const sortedTeamMembers = useMemo(
    () =>
      [...(members || [])].sort((a, b) =>
        String(a?.full_name || '').localeCompare(String(b?.full_name || ''), 'pt-BR', { sensitivity: 'base' })
      ),
    [members]
  );

  const sortedSongs = useMemo(
    () =>
      [...(songs || [])].sort((a, b) =>
        String(a?.title || '').localeCompare(String(b?.title || ''), 'pt-BR', { sensitivity: 'base' })
      ),
    [songs]
  );

  const normalizeIdentity = (value) => String(value || '').trim().toLowerCase();
  const validateScheduleTimeWindow = () =>
    validateRequiredScheduleTimes({
      schedule: formData,
      toast,
      extraTimeField: 'rehearsal_time',
      extraTimeLabel: 'horário de ensaio'
    });

  const findMemberScheduleConflict = (selectedMember) => {
    const schedulePools = [
      { type: 'worship', label: 'Louvor', items: schedules || [] },
      { type: 'media', label: 'Mídia', items: mediaSchedules || [] },
      { type: 'kids', label: 'Kids MBV', items: kidsSchedules || [] },
      { type: 'reception', label: 'Recepção', items: receptionSchedules || [] },
      { type: 'dance', label: 'Dança', items: danceSchedules || [] }
    ];

    return findScheduleMemberConflict({
      selectedMember,
      formSchedule: formData,
      editingSchedule,
      currentTeamType: 'worship',
      schedulePools
    });
  };

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.WorshipSchedule.create(data),
    onSuccess: (newSchedule) => {
      queryClient.invalidateQueries({ queryKey: ['worshipSchedules'] });
      toast.success('Escala criada com sucesso! Clique em "Notificar" para enviar aos escalados.');
      resetForm();
      
      // Abrir modal do WhatsApp após criar
      if (newSchedule.members?.length > 0) {
        setTimeout(() => setWhatsappModalSchedule(newSchedule), 300);
      }
    },
    onError: (error) => {
      toast.error(error?.message || 'Não foi possível criar a escala. Tente novamente.');
    }
  });

  const sendNotificationsMutation = useMutation({
    mutationFn: async (schedule) => {
      if (!schedule?.members?.length) return { notifiedCount: 0, missingRecipientCount: 0 };

      const selectedMemberIds = schedule.members.map((m) => m.id);
      const selectedMembers = members.filter((m) => selectedMemberIds.includes(m.id));
      const churchMembers = await base44.entities.Member.list('-created_date', 2000).catch(() => []);
      const churchMemberById = new Map(churchMembers.map((m) => [m.id, m]));

      const scheduleDate = formatScheduleDateLabel(schedule.date, "d 'de' MMMM");
      const title = `Nova Escala: ${schedule.title}`;
      const deliveries = [];
      let missingRecipientCount = 0;

      schedule.members.forEach((scaledMember) => {
        const { teamMember, recipientEmail, recipientMemberId } = resolveScheduleRecipient({
          scaledMember,
          selectedMembers,
          allTeamMembers: members,
          churchMemberById,
        });

        if (!teamMember || (!recipientMemberId && !recipientEmail)) {
          missingRecipientCount += 1;
          return;
        }

        const message = `Você foi escalado(a) para ${schedule.title} no dia ${scheduleDate}${schedule.start_time ? ` às ${schedule.start_time}` : ''}${scaledMember.role ? `\n\nFunção: ${scaledMember.role}` : ''}${schedule.rehearsal_time ? `\n\nEnsaio: ${schedule.rehearsal_time}` : ''}.${schedule.observations ? `\n\n${schedule.observations}` : ''}\n\nConfirmar Presença ou Solicitar Alteração em Escala`;

        deliveries.push(
          deliverMemberNotification({
            recipients: [{ member_id: recipientMemberId, member_email: recipientEmail }],
            title,
            message,
            type: 'worship_schedule',
            referenceId: schedule.id,
          })
        );

        if (!recipientEmail) return;

        deliveries.push(
          base44.integrations.Core.SendEmail({
            to: recipientEmail,
            subject: title,
            body: `
              <h2>${schedule.title}</h2>
              <p><strong>Data:</strong> ${scheduleDate}</p>
              ${schedule.start_time ? `<p><strong>Horário:</strong> ${schedule.start_time}${schedule.end_time ? ` - ${schedule.end_time}` : ''}</p>` : ''}
              ${scaledMember.role ? `<p><strong>Função:</strong> ${scaledMember.role}</p>` : ''}
              ${schedule.rehearsal_time ? `<p><strong>Ensaio:</strong> ${schedule.rehearsal_time}</p>` : ''}
              ${schedule.location ? `<p><strong>Local:</strong> ${schedule.location}</p>` : ''}
              ${schedule.observations ? `<p><strong>Observações:</strong> ${schedule.observations}</p>` : ''}
              ${schedule.songs?.length > 0 ? `<p><strong>Hinos:</strong> ${schedule.songs.map((s) => s.title).join(', ')}</p>` : ''}
            `
          })
        );
      });

      await Promise.allSettled(deliveries);

      return {
        notifiedCount: Math.max(0, schedule.members.length - missingRecipientCount),
        missingRecipientCount,
      };
    },
    onSuccess: ({ notifiedCount, missingRecipientCount }) => {
      if (notifiedCount > 0) {
        toast.success(`Notificações enviadas para ${notifiedCount} integrante(s).`);
      }
      if (missingRecipientCount > 0) {
        toast.warning(`${missingRecipientCount} integrante(s) sem vínculo/e-mail para receber notificações.`);
      }
    },
    onError: (error) => {
      console.error('Erro ao enviar notificações de escala (louvor):', error);
      toast.error('Não foi possível enviar as notificações agora.');
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.WorshipSchedule.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['worshipSchedules'] });
      toast.success('Escala atualizada com sucesso!');
      resetForm();
    },
    onError: (error) => {
      toast.error(error?.message || 'Não foi possível atualizar a escala.');
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.WorshipSchedule.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['worshipSchedules'] });
      toast.success('Escala excluída com sucesso!');
    }
  });

  const goToFormStep = (step) => {
    if (step > activeFormStep && step > 1 && (!formData.title.trim() || !formData.date)) {
      toast.error('Preencha título e data antes de continuar.');
      return;
    }
    if (step > activeFormStep && step > 1 && !validateScheduleTimeWindow()) return;
    if (step > activeFormStep && step > 2 && formData.members.some((member) => !member.role)) {
      toast.error('Escolha a função de cada integrante antes de continuar.');
      return;
    }
    formDialogRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    setActiveFormStep(Math.min(Math.max(step, 1), FORM_STEPS.length));
  };

  const resetForm = () => {
    setActiveFormStep(1);
    setFormData({
      title: '',
      date: '',
      start_time: '',
      end_time: '',
      location: '',
      audiences: [],
      rehearsal_time: '',
      observations: '',
      songs: [],
      members: []
    });
    setEditingSchedule(null);
    setShowForm(false);
  };

  const handleEdit = (schedule) => {
    setActiveFormStep(1);
    setEditingSchedule(schedule);
    setFormData({
      title: schedule.title || '',
      date: schedule.date || '',
      start_time: schedule.start_time || '',
      end_time: schedule.end_time || '',
      location: schedule.location || '',
      audiences: schedule.audiences || [],
      rehearsal_time: schedule.rehearsal_time || '',
      observations: schedule.observations || '',
      songs: schedule.songs || [],
      members: (schedule.members || []).map(m => ({
        ...m,
        role: m.role || null
      }))
    });
    setShowForm(true);
  };

  const handleSubmit = () => {
    if (!formData.title || !formData.date) {
      toast.error('Título e data são obrigatórios.');
      return;
    }
    if (!validateScheduleTimeWindow()) return;
    if (formData.members.some((member) => !member.role)) {
      toast.error('Escolha a função de cada integrante antes de salvar.');
      return;
    }

    // Validar se algum membro está indisponível na data selecionada
    const unavailableMembers = formData.members.filter((member) =>
      getAvailabilityBlockForMember(member, formData.date)
    );

    if (unavailableMembers.length > 0) {
      toast.error(UNAVAILABLE_MEMBER_MESSAGE);
      return;
    }
    const memberConflict = formData.members
      .map((member) => findMemberScheduleConflict(member))
      .find(Boolean);
    if (memberConflict) {
      toast.error('Este integrante já está escalado em outra escala neste horário.');
      return;
    }

    // Garantir que todos os campos estão presentes
    const dataToSave = {
      ...formData,
      members: formData.members.map(m => ({
        id: m.id,
        name: m.name,
        member_id: m.member_id || null,
        role: m.role || null,
        confirmed: m.confirmed || false,
        confirmed_at: m.confirmed_at || null,
        member_email: m.member_email || ''
      }))
    };

    if (editingSchedule) {
      updateMutation.mutate({ id: editingSchedule.id, data: dataToSave });
    } else {
      createMutation.mutate(dataToSave);
    }
  };

  const toggleSong = (songId) => {
    const song = songs.find(s => s.id === songId);
    if (!song) return;

    const exists = formData.songs.some(s => s.id === songId);
    const updated = exists
      ? formData.songs.filter(s => s.id !== songId)
      : [...formData.songs, { id: song.id, title: song.title, key: song.key }];
    setFormData({ ...formData, songs: updated });
  };

  const toggleMember = (memberId) => {
    const member = members.find(m => m.id === memberId);
    if (!member) return;

    const exists = formData.members.some(m => m.id === memberId);
    if (!exists && !validateScheduleTimeWindow()) return;
    if (exists) {
      setFormData({ ...formData, members: formData.members.filter(m => m.id !== memberId) });
      return;
    }

    // Verificar se o membro está bloqueado na data selecionada
    if (formData.date) {
      const block = getAvailabilityBlockForMember(member, formData.date);
      const isBlocked = !!block;
      
      if (isBlocked) {
        toast.error(UNAVAILABLE_MEMBER_MESSAGE);
        return;
      }
    }

    if (!exists && formData.date) {
      const memberToAdd = {
        id: member.id,
        name: member.full_name,
        member_id: member.member_id || null,
        member_email: member.email || ''
      };
      const memberConflict = findMemberScheduleConflict(memberToAdd);
      if (memberConflict) {
        toast.error('Este integrante já está escalado em outra escala neste horário.');
        return;
      }
    }

    const updated = [...formData.members, { 
          id: member.id, 
          name: member.full_name,
          member_id: member.member_id || null,
          role: null,
          confirmed: false,
          member_email: member.email
        }];
    setFormData({ ...formData, members: updated });
  };

  const { data: currentMember } = useQuery({
    queryKey: ['currentMember', user?.id, user?.email],
    queryFn: () => getMemberByUser(user),
    enabled: !!user
  });

  const { data: worshipMember } = useQuery({
    queryKey: ['currentWorshipMember', user?.id, user?.email],
    queryFn: () => getWorshipTeamMemberByUser(user),
    enabled: !!user
  });

  const canConfirmPresence = (memberInSchedule) => {
    if (isAdmin) return true;
    if (!worshipMember) return false;
    return isSameTeamMember(memberInSchedule, worshipMember) || isSameTeamMember(memberInSchedule, currentMember);
  };

  const toggleMemberConfirmation = (scheduleId, memberId) => {
    const schedule = schedules.find(s => s.id === scheduleId);
    if (!schedule) return;

    const memberInSchedule = schedule.members.find(m => m.id === memberId);
    if (!memberInSchedule) return;
    if (!canConfirmPresence(memberInSchedule)) {
      toast.error('Você só pode confirmar sua própria presença');
      return;
    }

    const isConfirming = !memberInSchedule.confirmed;
    if (!isConfirming) {
      toast.error('Presença confirmada não pode ser desfeita diretamente. Solicite alteração da escala.');
      return;
    }

    if (isConfirming && isScheduleConfirmationClosed(schedule)) {
      toast.error('O prazo para confirmar presença nesta escala já passou.');
      return;
    }

    const updatedMembers = schedule.members.map(m => 
      m.id === memberId ? { 
        ...m, 
        confirmed: !m.confirmed,
        confirmed_at: !m.confirmed ? new Date().toISOString() : null
      } : m
    );

    const scheduleDateLabel = formatScheduleDateLabel(schedule.date, "d 'de' MMMM 'de' yyyy");
    const title = 'Presença confirmada (Louvor)';
    const message = `${memberInSchedule.name} confirmou presença na escala "${schedule.title}" (${scheduleDateLabel}).`;
    confirmPresenceMutation.mutate(
      { id: scheduleId, members: updatedMembers },
      { onSuccess: () => {
        notifyTeamLeaders({
          base44,
          leaders,
          churchMembers,
          teamLinkKey: 'link_to_worship',
          title,
          message,
          type: 'worship_schedule',
          reference_id: schedule.id
        }).catch((error) => {
          console.error('Erro ao notificar líderes de louvor:', error);
        });
      } },
    );
  };

  const requestScheduleChange = async (schedule, memberInSchedule) => {
    if (!schedule || !memberInSchedule) return;
    if (!canConfirmPresence(memberInSchedule)) {
      toast.error('Você só pode solicitar alteração da sua própria escala');
      return;
    }

    const reason = window.prompt('Descreva o motivo da solicitação de alteração:');
    if (!reason || !reason.trim()) {
      toast.error('Informe o motivo para solicitar alteração.');
      return;
    }

    const scheduleDateLabel = formatScheduleDateLabel(schedule.date, "d 'de' MMMM 'de' yyyy");
    const title = 'Solicitação de alteração de escala (Louvor)';
    const message =
      `${memberInSchedule.name} solicitou alteração na escala "${schedule.title}" (${scheduleDateLabel}).\n` +
      `Motivo: ${reason.trim()}` +
      (memberInSchedule.confirmed ? '\nA confirmação de presença foi removida automaticamente.' : '');

    try {
      const result = await notifyTeamLeaders({
        base44,
        leaders,
        churchMembers,
        teamLinkKey: 'link_to_worship',
        title,
        message,
        type: 'worship_schedule',
        reference_id: schedule.id
      });

      if (!result.notified) {
        toast.warning('Nenhum líder de louvor ativo encontrado para receber a solicitação.');
        return;
      }

      if (memberInSchedule.confirmed) {
        const updatedMembers = schedule.members.map((member) =>
          member.id === memberInSchedule.id
            ? { ...member, confirmed: false, confirmed_at: null }
            : member
        );
        updateMutation.mutate({
          id: schedule.id,
          data: { ...schedule, members: updatedMembers }
        });
      }

      toast.success(memberInSchedule.confirmed
        ? 'Solicitação enviada e presença desconfirmada automaticamente.'
        : 'Solicitação enviada ao líder da equipe.');
    } catch (error) {
      console.error('Erro ao enviar solicitação de alteração (louvor):', error);
      toast.error('Não foi possível enviar a solicitação agora.');
    }
  };

  const filteredSchedules = React.useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const normalizedUserEmail = normalizeIdentity(user?.email);
    const now = new Date();
    const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endToday = new Date(startToday);
    endToday.setDate(endToday.getDate() + 1);
    const endWeek = new Date(startToday);
    endWeek.setDate(endWeek.getDate() + 7);
    const endMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);

    return (schedules || []).filter((schedule) => {
      const scheduleDate = parseScheduleDate(schedule.date);
      const title = String(schedule.title || '').toLowerCase();
      const location = String(schedule.location || '').toLowerCase();
      const matchesSearch = !term || title.includes(term) || location.includes(term);

      let matchesQuickFilter = true;
      if (!isValidDateValue(scheduleDate)) {
        return matchesSearch && quickFilter === 'all';
      }
      if (quickFilter === 'today') matchesQuickFilter = scheduleDate >= startToday && scheduleDate < endToday;
      if (quickFilter === 'week') matchesQuickFilter = scheduleDate >= startToday && scheduleDate < endWeek;
      if (quickFilter === 'month') matchesQuickFilter = scheduleDate >= startToday && scheduleDate < endMonth;
      if (quickFilter === 'upcoming') matchesQuickFilter = scheduleDate >= startToday;
      if (quickFilter === 'mine') {
        const currentMemberId = String(currentMember?.id || '');
        const currentTeamMemberId = String(worshipMember?.id || '');
        matchesQuickFilter = (schedule.members || []).some((memberInSchedule) => {
          const scheduleMemberId = String(memberInSchedule?.member_id || '');
          const scheduleTeamMemberId = String(memberInSchedule?.id || '');
          const scheduleMemberEmail = normalizeIdentity(memberInSchedule?.member_email || memberInSchedule?.email);
          return (
            (currentMemberId && scheduleMemberId === currentMemberId) ||
            (currentTeamMemberId && scheduleTeamMemberId === currentTeamMemberId) ||
            (normalizedUserEmail && scheduleMemberEmail === normalizedUserEmail)
          );
        });
      }

      return matchesSearch && matchesQuickFilter;
    });
  }, [schedules, searchTerm, quickFilter, user?.email, currentMember?.id, worshipMember?.id]);

  const orderedSchedules = React.useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const normalized = (filteredSchedules || []).map((schedule) => ({
      ...schedule,
      __isPast: getScheduleTimestamp(schedule.date, Number.POSITIVE_INFINITY) < today.getTime(),
    }));

    const upcoming = normalized
      .filter((schedule) => !schedule.__isPast)
      .sort((a, b) => getScheduleTimestamp(a.date, Number.POSITIVE_INFINITY) - getScheduleTimestamp(b.date, Number.POSITIVE_INFINITY));

    const past = normalized
      .filter((schedule) => schedule.__isPast)
      .sort((a, b) => getScheduleTimestamp(b.date, Number.NEGATIVE_INFINITY) - getScheduleTimestamp(a.date, Number.NEGATIVE_INFINITY));

    return [...upcoming, ...past];
  }, [filteredSchedules]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-green-600 to-emerald-700 text-white pt-8 pb-6 px-4">
        <div className="max-w-lg mx-auto">
          <PageHeader 
            title="Escalas" 
            subtitle={`${schedules.length} programadas`}
            showBack={true}
            action={
              <div className="flex gap-2">
                {canManageWorship && (
                  <Button onClick={() => setShowForm(true)} size="sm" className="bg-white text-green-700 hover:bg-green-50">
                    <Plus className="w-4 h-4 mr-1" />
                    Nova
                  </Button>
                )}
              </div>
            }
          />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-3 sm:px-4 py-4 sm:py-6">
        <div className="mbv-schedule-filters bg-white border border-gray-200 rounded-2xl p-3 mb-4 shadow-sm space-y-3">
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por título ou local"
            className="h-10"
          />
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 flex items-center gap-1.5">
              <Filter className="w-3.5 h-3.5" />
              Filtros rápidos
            </p>
            <Select value={quickFilter} onValueChange={setQuickFilter}>
              <SelectTrigger className="h-10">
                <SelectValue placeholder="Selecione um filtro" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="today">Hoje</SelectItem>
                <SelectItem value="week">Semana</SelectItem>
                <SelectItem value="month">Mês</SelectItem>
                <SelectItem value="upcoming">Próximas escalas</SelectItem>
                <SelectItem value="mine">Minhas escalas</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="border-t border-gray-100 pt-3">
            <button
              type="button"
              className="w-full text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 flex items-center justify-between"
              onClick={() => setShowViewModePanel((prev) => !prev)}
            >
              <span className="flex items-center gap-1.5">
                <SlidersHorizontal className="w-3.5 h-3.5" />
                Modo de visualização
              </span>
              <ChevronDown className={`w-4 h-4 transition-transform sm:hidden ${showViewModePanel ? 'rotate-180' : ''}`} />
            </button>
          </div>
          <div className={`${showViewModePanel ? 'flex' : 'hidden'} gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:grid sm:grid-cols-3 sm:gap-2 sm:overflow-visible sm:pb-0`}>
            <Button
              type="button"
              size="sm"
              variant={viewMode === 'compact' ? 'default' : 'outline'}
              className="shrink-0 min-w-[112px] sm:min-w-0 sm:w-full"
              onClick={() => setViewMode('compact')}
            >
              Compacto
            </Button>
            <Button
              type="button"
              size="sm"
              variant={viewMode === 'detailed' ? 'default' : 'outline'}
              className="shrink-0 min-w-[112px] sm:min-w-0 sm:w-full"
              onClick={() => setViewMode('detailed')}
            >
              Detalhado
            </Button>
            <Button
              type="button"
              size="sm"
              variant={showCalendarView ? 'default' : 'outline'}
              className="shrink-0 min-w-[140px] sm:min-w-0 sm:w-full"
              onClick={() => setShowCalendarView((prev) => !prev)}
            >
              {showCalendarView ? 'Ver Lista' : 'Ver Calendário'}
            </Button>
          </div>
        </div>

        {schedules.length === 0 ? (
          <EmptyState
            icon={Calendar}
            title="Nenhuma escala encontrada"
            description="Comece criando sua primeira escala"
            actionLabel={canManageWorship ? "Nova Escala" : undefined}
            onAction={canManageWorship ? () => setShowForm(true) : undefined}
          />
        ) : orderedSchedules.length === 0 ? (
          <EmptyState
            icon={Calendar}
            title="Nenhum resultado"
            description="Ajuste os filtros ou limpe a busca para ver mais escalas."
          />
        ) : showCalendarView ? (
          <CalendarView 
            schedules={orderedSchedules}
            availabilityBlocks={availabilityBlocks}
            members={members}
            onScheduleClick={(schedule) => setViewingSchedule(schedule)}
          />
        ) : (
          <div className="mbv-schedule-list">
            {orderedSchedules.map((schedule, index) => {
              const isCompact = viewMode === 'compact';
              return (
              <React.Fragment key={schedule.id}>
                {(index === 0 || orderedSchedules[index - 1].__isPast !== schedule.__isPast) && (
                  <div className="pt-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {schedule.__isPast ? 'Escalas realizadas' : 'Proximas escalas'}
                    </p>
                  </div>
                )}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className={`mbv-schedule-card bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-all overflow-hidden ${schedule.__isPast ? 'opacity-80' : ''}`}
              >
                {/* Header colorido */}
                <div className="mbv-schedule-card-header bg-gradient-to-r from-green-600 to-emerald-600 p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Calendar className="w-4 h-4 text-green-100 flex-shrink-0" />
                        <h3 className="font-bold text-white text-lg truncate">{schedule.title}</h3>
                      </div>
                      <p className="text-sm text-green-100 font-medium">
                        {formatScheduleDateLabel(schedule.date, "EEEE, d 'de' MMMM")}
                      </p>
                    </div>
                  </div>
                  
                  {/* Info rápida */}
                  <div className="flex flex-wrap gap-2 mt-3">
                    {schedule.start_time && (
                      <div className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5 text-white" />
                        <span className="text-xs text-white font-semibold">{schedule.start_time}</span>
                      </div>
                    )}
                    {schedule.location && (
                      <div className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5 text-white" />
                        <span className="text-xs text-white font-semibold truncate max-w-[120px]">{schedule.location}</span>
                      </div>
                    )}
                    {schedule.songs?.length > 0 && (
                      <div className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full flex items-center gap-1.5">
                        <Music className="w-3.5 h-3.5 text-white" />
                        <span className="text-xs text-white font-semibold">{schedule.songs.length} hinos</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Corpo do card */}
                <div className="p-4">
                  {/* Ensaio destacado */}
                  {!isCompact && schedule.rehearsal_time && (
                    <div className="mb-3 p-3 bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200 rounded-xl">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-orange-600 flex items-center justify-center">
                          <Clock className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="text-[10px] font-bold text-orange-600 uppercase">Ensaio</p>
                          <p className="text-sm font-bold text-orange-900">{schedule.rehearsal_time}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Integrantes */}
                  {schedule.members?.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-2 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <UsersIcon className="w-4 h-4 text-indigo-600" />
                          <span className="text-xs font-bold text-indigo-900">Equipe</span>
                        </div>
                        <Badge className="bg-green-600 text-white text-xs font-bold">
                          {schedule.members.filter(m => m.confirmed).length}/{schedule.members.length}
                        </Badge>
                      </div>
                      
                      {!isCompact && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {schedule.members.map(member => {
                         const canConfirm = canConfirmPresence(member);
                         return (
                           <button
                             key={member.id}
                             onClick={() => canConfirm && !member.confirmed && toggleMemberConfirmation(schedule.id, member.id)}
                             disabled={!canConfirm || member.confirmed || confirmPresenceMutation.isPending}
                             className={`flex items-start gap-2 p-2.5 rounded-xl transition-all text-left ${
                               member.confirmed 
                                 ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-400 shadow-sm' 
                                 : 'bg-gradient-to-br from-gray-50 to-slate-50 border-2 border-gray-300'
                             } ${canConfirm && !member.confirmed ? 'hover:shadow-md cursor-pointer active:scale-95' : 'opacity-60 cursor-not-allowed'}`}
                           >
                             {member.confirmed ? (
                               <div className="w-5 h-5 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0">
                                 <CheckCircle className="w-3.5 h-3.5 text-white" />
                               </div>
                             ) : (
                               <div className="w-5 h-5 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0">
                                 <Circle className="w-3.5 h-3.5 text-white" />
                               </div>
                             )}
                             {scheduleMemberPhotoById.get(member.id) ? (
                               <img
                                 src={scheduleMemberPhotoById.get(member.id)}
                                 alt={member.name}
                                 className="w-7 h-7 rounded-full object-cover border border-white flex-shrink-0"
                               />
                             ) : (
                               <div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 border border-indigo-200 flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                                 {member.name?.charAt(0)?.toUpperCase() || 'I'}
                               </div>
                             )}
                             <div className="flex-1 min-w-0">
                               <p className={`text-xs font-bold truncate ${member.confirmed ? 'text-green-900' : 'text-gray-900'}`}>
                                 {member.name}
                               </p>
                               {member.role ? (
                                 <span className="text-[9px] text-white font-bold truncate mt-1 bg-gradient-to-r from-indigo-600 to-purple-600 px-2 py-0.5 rounded-md inline-block">
                                   {member.role}
                                 </span>
                               ) : (
                                 <span className="text-[9px] text-gray-500 font-medium truncate mt-1 bg-gray-200 px-1.5 py-0.5 rounded inline-block italic">
                                   Sem função
                                 </span>
                               )}
                             </div>
                           </button>
                         );
                        })}
                        </div>
                      )}
                      {isCompact && (
                        <p className="text-xs text-gray-500">Modo compacto ativo. Abra em detalhes para ver integrantes.</p>
                      )}
                    </div>
                  )}

                  {/* Botões de ação */}
                  <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t-2 border-gray-100">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="w-full sm:flex-1 bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700 sm:bg-transparent sm:text-foreground sm:border-input sm:hover:bg-accent sm:hover:text-accent-foreground"
                      onClick={() => setViewingSchedule(schedule)}
                    >
                      <Eye className="w-3.5 h-3.5 mr-1" />
                      Ver
                    </Button>
                    {canManageWorship && schedule.members?.length > 0 && (
                      <Button 
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 w-full sm:flex-1"
                        onClick={() => setWhatsappModalSchedule(schedule)}
                      >
                        <MessageCircle className="w-3.5 h-3.5 mr-1" />
                        WhatsApp
                      </Button>
                    )}
                    {canSendScheduleNotifications && schedule.members?.length > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full sm:flex-1"
                        onClick={() => sendNotificationsMutation.mutate(schedule)}
                        disabled={sendNotificationsMutation.isPending}
                      >
                        <BellRing className="w-3.5 h-3.5 mr-1" />
                        Notificar
                      </Button>
                    )}
                    {canManageWorship && (
                      <div className="ml-auto flex gap-2">
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => handleEdit(schedule)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => {
                            if (confirm('Deseja excluir esta escala?')) {
                              deleteMutation.mutate(schedule.id);
                            }
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
              </React.Fragment>
            );
            })}
          </div>
        )}
      </div>

      {/* Song Lyrics Dialog */}
      <Dialog open={!!viewingSongLyrics} onOpenChange={() => setViewingSongLyrics(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{viewingSongLyrics?.title}</DialogTitle>
          </DialogHeader>
          {viewingSongLyrics && (
            <div className="space-y-4 mt-4">
              {viewingSongLyrics.artist && (
                <p className="text-sm text-gray-600">Artista: {viewingSongLyrics.artist}</p>
              )}
              {viewingSongLyrics.key && (
                <p className="text-sm text-gray-600">Tom: <Badge variant="secondary">{viewingSongLyrics.key}</Badge></p>
              )}
              <div className="bg-gray-50 rounded-lg p-4">
                <pre className="text-sm whitespace-pre-wrap font-mono">{viewingSongLyrics.lyrics}</pre>
              </div>
              <div className="flex gap-2">
                {viewingSongLyrics.spotify_link && (
                  <a 
                    href={viewingSongLyrics.spotify_link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex-1"
                  >
                    <Button variant="outline" className="w-full bg-green-50 hover:bg-green-100">
                      <Music className="w-4 h-4 mr-2" />
                      Spotify
                    </Button>
                  </a>
                )}
                {viewingSongLyrics.youtube_link && (
                  <a 
                    href={viewingSongLyrics.youtube_link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex-1"
                  >
                    <Button variant="outline" className="w-full bg-red-50 hover:bg-red-100">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      YouTube
                    </Button>
                  </a>
                )}
              </div>
              <Button variant="outline" className="w-full" onClick={() => setViewingSongLyrics(null)}>
                Fechar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={!!viewingSchedule} onOpenChange={closeViewingSchedule}>
        <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[85vh] overflow-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">Detalhes da Escala</DialogTitle>
          </DialogHeader>

          {viewingSchedule && (
            <div className="space-y-4 mt-2">
              {/* Cabeçalho com gradiente */}
              <div className="bg-gradient-to-br from-green-600 to-emerald-700 text-white rounded-xl p-4 shadow-lg">
                <h3 className="font-bold text-base sm:text-xl mb-1">{viewingSchedule.title}</h3>
                <p className="text-sm text-green-100 flex items-center gap-2">
                  <Calendar className="w-4 h-4 flex-shrink-0" />
                  <span className="break-words">{formatScheduleDateLabel(viewingSchedule.date, "EEE, d 'de' MMM 'de' yyyy")}</span>
                </p>
              </div>

              {/* Cards de informação */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                {viewingSchedule.start_time && (
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3 border border-blue-200">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-blue-600 flex-shrink-0" />
                      <p className="text-xs font-semibold text-blue-900">Horário</p>
                    </div>
                    <p className="font-bold text-sm text-blue-900">{viewingSchedule.start_time} {viewingSchedule.end_time && `- ${viewingSchedule.end_time}`}</p>
                  </div>
                )}
                {viewingSchedule.rehearsal_time && (
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-3 border border-orange-200">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-orange-600 flex-shrink-0" />
                      <p className="text-xs font-semibold text-orange-900">Ensaio</p>
                    </div>
                    <p className="font-bold text-sm text-orange-900">{viewingSchedule.rehearsal_time}</p>
                  </div>
                )}
                {viewingSchedule.location && (
                  <div className="sm:col-span-2 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-3 border border-purple-200">
                    <div className="flex items-center gap-2 mb-1">
                      <MapPin className="w-4 h-4 text-purple-600 flex-shrink-0" />
                      <p className="text-xs font-semibold text-purple-900">Local</p>
                    </div>
                    <p className="font-bold text-sm text-purple-900 break-words">{viewingSchedule.location}</p>
                  </div>
                )}
              </div>

              {viewingSchedule.audiences?.length > 0 && (
                <div className="bg-white rounded-xl p-3 sm:p-4 border-2 border-gray-200">
                  <div className="flex items-center gap-2 mb-2">
                    <UsersIcon className="w-4 h-4 text-indigo-600" />
                    <p className="text-xs sm:text-sm font-bold text-gray-900">Público-Alvo</p>
                  </div>
                  <div className="flex flex-wrap gap-1.5 sm:gap-2">
                    {viewingSchedule.audiences.map((aud, idx) => (
                      <Badge key={idx} className="bg-indigo-100 text-indigo-900 border-indigo-300 px-2 sm:px-3 py-0.5 sm:py-1 text-xs">
                        {aud.type === 'Todos' ? 'Todos' : aud.name || aud.type}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {viewingSchedule.members?.length > 0 && (
                <div className="bg-white rounded-xl p-3 sm:p-4 border-2 border-gray-200">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2">
                      <UsersIcon className="w-4 h-4 text-green-600" />
                      <p className="text-xs sm:text-sm font-bold text-gray-900">Integrantes Escalados</p>
                    </div>
                    <Badge className="bg-green-100 text-green-900 border-green-300 px-2 sm:px-3 py-1 text-xs font-bold self-start sm:self-auto">
                      {viewingSchedule.members.filter(m => m.confirmed).length}/{viewingSchedule.members.length} confirmados
                    </Badge>
                  </div>
                  <div className="space-y-2 sm:space-y-3">
                    {viewingSchedule.members.map((member, idx) => {
                      const canConfirm = canConfirmPresence(member);
                      
                      return (
                        <div 
                          key={idx} 
                          className={`p-3 sm:p-4 rounded-lg sm:rounded-xl border-2 transition-all ${
                            member.confirmed 
                              ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-400 shadow-sm' 
                              : 'bg-gradient-to-br from-gray-50 to-slate-50 border-gray-300'
                          }`}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                            <div className="flex items-start gap-2 sm:gap-3 flex-1">
                              <div className={`p-2 sm:p-2.5 rounded-lg sm:rounded-xl ${member.confirmed ? 'bg-green-600' : 'bg-gray-300'} shadow-sm flex-shrink-0`}>
                                {member.confirmed ? (
                                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                                ) : (
                                  <Circle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                                )}
                              </div>
                              {scheduleMemberPhotoById.get(member.id) ? (
                                <img
                                  src={scheduleMemberPhotoById.get(member.id)}
                                  alt={member.name}
                                  className="w-10 h-10 rounded-full object-cover border border-white shadow-sm flex-shrink-0"
                                />
                              ) : (
                                <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 border border-indigo-200 flex items-center justify-center font-bold text-sm flex-shrink-0">
                                  {member.name?.charAt(0)?.toUpperCase() || 'I'}
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm sm:text-base font-bold mb-2 ${member.confirmed ? 'text-green-900' : 'text-gray-900'} break-words`}>
                                  {member.name}
                                </p>
                                
                                {member.role ? (
                                  <div className="mb-2">
                                    <span className="text-[10px] sm:text-xs font-bold text-white bg-gradient-to-r from-indigo-600 to-purple-600 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg inline-flex items-center gap-1 sm:gap-1.5 shadow-sm">
                                      <Music className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                                      {member.role}
                                    </span>
                                  </div>
                                ) : (
                                  <div className="mb-2">
                                    <span className="text-[10px] sm:text-xs font-medium text-gray-500 bg-gray-200 px-2 py-0.5 sm:py-1 rounded-md inline-block italic">
                                      Sem função
                                    </span>
                                  </div>
                                )}
                                {member.confirmed && member.confirmed_at && (
                                  <div className="mt-2 p-2 bg-green-600/20 rounded-lg border border-green-600/30">
                                    <p className="text-[10px] sm:text-xs font-bold text-green-800 flex items-center gap-1">
                                      <CheckCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                                      <span className="break-words">Confirmado em {formatDateTimeLabel(member.confirmed_at, "dd/MM/yy HH:mm")}</span>
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col gap-2 w-full sm:w-auto">
                              <button
                                onClick={() => canConfirm && !member.confirmed && toggleMemberConfirmation(viewingSchedule.id, member.id)}
                                disabled={!canConfirm || member.confirmed || confirmPresenceMutation.isPending}
                                className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl font-bold text-[10px] sm:text-xs transition-all shadow-sm w-full sm:w-auto ${
                                  member.confirmed
                                    ? 'bg-green-100 text-green-700 border border-green-200 cursor-not-allowed'
                                    : canConfirm
                                      ? 'bg-gradient-to-br from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 active:scale-95'
                                    : 'opacity-50 cursor-not-allowed bg-gray-300 text-gray-500'
                                }`}
                                title={member.confirmed ? 'Presença já confirmada. Para sair da escala, solicite alteração.' : !canConfirm ? 'Apenas o integrante ou admin pode confirmar' : ''}
                              >
                                {member.confirmed ? 'Confirmado' : 'Confirmar Presença'}
                              </button>
                              {canConfirm && (
                                <button
                                  onClick={() => requestScheduleChange(viewingSchedule, member)}
                                  className="px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl font-bold text-[10px] sm:text-xs transition-all shadow-sm w-full sm:w-auto bg-amber-500 text-white hover:bg-amber-600 active:scale-95"
                                >
                                  Solicitar Alteração
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {viewingSchedule.songs?.length > 0 && (
                <div className="bg-white rounded-xl p-3 sm:p-4 border-2 border-gray-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Music className="w-4 h-4 text-pink-600" />
                    <p className="text-xs sm:text-sm font-bold text-gray-900">Hinos da Escala</p>
                  </div>
                  <div className="space-y-2 sm:space-y-3">
                    {viewingSchedule.songs.map((song, idx) => {
                      const fullSong = songs.find(s => s.id === song.id);
                      return (
                        <div key={idx} className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-lg p-3 border border-pink-200">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-lg bg-pink-600 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                                {idx + 1}
                              </div>
                              <span className="text-xs sm:text-sm font-bold text-gray-900 break-words">{song.title}</span>
                            </div>
                            {song.key && <Badge className="bg-pink-600 text-white border-0 px-2 py-0.5 text-[10px] sm:text-xs font-bold self-start sm:self-auto">{song.key}</Badge>}
                          </div>
                          {fullSong && (
                            <div className="flex flex-wrap gap-1.5 sm:gap-2">
                              {fullSong.spotify_link && (
                                <a 
                                  href={fullSong.spotify_link} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-[10px] sm:text-xs bg-green-600 text-white px-2 sm:px-3 py-1.5 sm:py-2 rounded-md sm:rounded-lg hover:bg-green-700 transition-all flex items-center gap-1 sm:gap-1.5 font-semibold shadow-sm"
                                >
                                  <Music className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                                  Spotify
                                </a>
                              )}
                              {fullSong.youtube_link && (
                                <a 
                                  href={fullSong.youtube_link} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-[10px] sm:text-xs bg-red-600 text-white px-2 sm:px-3 py-1.5 sm:py-2 rounded-md sm:rounded-lg hover:bg-red-700 transition-all flex items-center gap-1 sm:gap-1.5 font-semibold shadow-sm"
                                >
                                  <ExternalLink className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                                  YouTube
                                </a>
                              )}
                              {fullSong.lyrics && (
                                <button
                                  onClick={() => setViewingSongLyrics(fullSong)}
                                  className="text-[10px] sm:text-xs bg-indigo-600 text-white px-2 sm:px-3 py-1.5 sm:py-2 rounded-md sm:rounded-lg hover:bg-indigo-700 transition-all flex items-center gap-1 sm:gap-1.5 font-semibold shadow-sm"
                                >
                                  <Music className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                                  Cifra
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {viewingSchedule.observations && (
                <div className="mbv-schedule-observations rounded-lg sm:rounded-xl p-3 sm:p-4 border-2">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-base sm:text-lg"></span>
                    <p className="text-xs sm:text-sm font-bold text-amber-900">Observações</p>
                  </div>
                  <p className="text-xs sm:text-sm text-amber-900 leading-relaxed font-medium break-words">{viewingSchedule.observations}</p>
                </div>
              )}

              <div className="space-y-2 sm:space-y-3 pt-3 sm:pt-4">
                <div className="flex flex-col sm:flex-row gap-2">
	                  <Button 
	                    size="sm"
	                    variant="outline" 
	                    className="flex-1"
	                    onClick={async () => {
	                      try {
	                        const scheduleForExport = { ...viewingSchedule, type: 'worship' };
	                        if (isNativeIOS()) {
	                          const nativeEvent = buildNativeCalendarEventFromSchedule(scheduleForExport);
	                          if (nativeEvent) {
	                            await importEventsToNativeCalendar([{
	                              ...nativeEvent,
	                              notes: nativeEvent.notes || 'Equipe de Louvor - MBV',
	                            }]);
	                            toast.success('Escala adicionada na agenda do iPhone!');
	                            return;
	                          }
	                        }

	                        await downloadICSFile(
	                          buildICSFromSchedule(scheduleForExport),
	                          `escala-${viewingSchedule.title.replace(/\s+/g, '-')}.ics`
	                        );
	                        toast.success('Arquivo .ics exportado!');
	                      } catch (error) {
	                        toast.error(error?.message || 'Erro ao exportar');
	                      }
	                    }}
	                  >
	                    <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-1" />
	                    {isNativeIOS() ? 'Adicionar Agenda' : <><span className="hidden sm:inline">Exportar</span> .ics</>}
	                  </Button>
                  <Button 
                    size="sm"
                    variant="outline" 
                    className="flex-1"
                    onClick={async () => {
                      try {
                        const response = await base44.functions.invoke('syncToGoogleCalendar', {
                          schedule: {...viewingSchedule, type: 'worship'}
                        });
                        
                        if (response.data.success) {
                          toast.success('Sincronizado!');
                          if (response.data.event_link) {
                            window.open(response.data.event_link, '_blank');
                          }
                        }
                      } catch (error) {
                        toast.error('Erro ao sincronizar');
                      }
                    }}
                  >
                    <CalendarDays className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-1" />
                    <span className="hidden sm:inline">Google</span>
                  </Button>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button variant="outline" className="flex-1" onClick={closeViewingSchedule}>
                    Fechar
                  </Button>
                  {canManageWorship && viewingSchedule.members?.length > 0 && (
                    <Button 
                      variant="outline"
                      className="flex-1 bg-green-50 text-green-700 hover:bg-green-100 border-green-200"
                        onClick={() => {
                          setWhatsappModalSchedule(viewingSchedule);
                          closeViewingSchedule();
                        }}
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      WhatsApp
                    </Button>
                  )}
                  {canManageWorship && (
                    <Button 
                      className="flex-1" 
                      onClick={() => {
                        handleEdit(viewingSchedule);
                        closeViewingSchedule();
                      }}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Editar
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* WhatsApp Modal */}
      <Dialog open={!!whatsappModalSchedule} onOpenChange={() => setWhatsappModalSchedule(null)}>
        <DialogContent className="mbv-schedule-whatsapp max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Enviar WhatsApp - {whatsappModalSchedule?.title}</DialogTitle>
          </DialogHeader>

          {whatsappModalSchedule && (
            <div className="space-y-4 mt-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800">
                  <strong>Instruções:</strong> Use o envio coletivo para escolher um grupo no WhatsApp ou clique no botão ao lado de cada membro para mensagem individual.
                </p>
              </div>
              {(() => {
                const scheduleDate = formatScheduleDateLabel(whatsappModalSchedule.date, "d 'de' MMMM");
                const collectiveMessage = buildCollectiveScheduleWhatsAppMessage({
                  title: 'ESCALA DE LOUVOR',
                  schedule: whatsappModalSchedule,
                  scheduleDate,
                  includeSongs: true,
                  closing: 'Deus abencoe nosso louvor!',
                });
                const collectiveUrl = buildWhatsAppShareUrl(collectiveMessage);

                return (
                  <div className="bg-white border border-green-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-gray-900">Mensagem coletiva</p>
                      <p className="text-sm text-gray-600">Abre o WhatsApp com a escala completa para selecionar o grupo.</p>
                    </div>
                    <a href={collectiveUrl} target="_blank" rel="noopener noreferrer">
                      <Button type="button" className="bg-green-600 hover:bg-green-700 w-full sm:w-auto">
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Enviar para grupo
                      </Button>
                    </a>
                  </div>
                );
              })()}
              {(() => {
                const songsWithLinks = (whatsappModalSchedule.songs || [])
                  .map((songItem) => {
                    const fullSong = songs.find((librarySong) => librarySong.id === songItem.id);
                    return {
                      id: songItem.id,
                      title: songItem.title,
                      spotify_link: fullSong?.spotify_link || '',
                      youtube_link: fullSong?.youtube_link || ''
                    };
                  })
                  .filter((songItem) => songItem.spotify_link || songItem.youtube_link);

                if (songsWithLinks.length === 0) return null;

                return (
                  <div className="bg-white border rounded-lg p-4 space-y-3">
                    <p className="text-sm font-semibold text-gray-900">Links para estudo</p>
                    {songsWithLinks.map((songItem) => (
                      <div key={songItem.id} className="flex items-center justify-between gap-3 border rounded-md p-2">
                        <p className="text-sm text-gray-800 truncate">{songItem.title}</p>
                        <div className="flex items-center gap-2">
                          {songItem.youtube_link && (
                            <a href={songItem.youtube_link} target="_blank" rel="noopener noreferrer">
                              <Button type="button" size="sm" variant="outline" className="gap-1">
                                <Youtube className="w-4 h-4 text-red-600" />
                                YouTube
                              </Button>
                            </a>
                          )}
                          {songItem.spotify_link && (
                            <a href={songItem.spotify_link} target="_blank" rel="noopener noreferrer">
                              <Button type="button" size="sm" variant="outline" className="gap-1">
                                <Music2 className="w-4 h-4 text-green-600" />
                                Spotify
                              </Button>
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}

              <div className="space-y-3">
                {whatsappModalSchedule.members?.map((member, idx) => {
                  const fullMember = members.find(m => m.id === member.id);
                  const phone = fullMember?.phone;
                  
                  const scheduleDate = formatScheduleDateLabel(whatsappModalSchedule.date, "d 'de' MMMM");
                  
                  let message = `*ESCALA DE LOUVOR*\n`;
                  message += `"""""""""""""""""""\n\n`;
                  message += `*${whatsappModalSchedule.title}*\n\n`;
                  message += `*Data:* ${scheduleDate}\n`;
                  if (whatsappModalSchedule.start_time) {
                    message += `*Horário:* ${whatsappModalSchedule.start_time}`;
                    if (whatsappModalSchedule.end_time) message += ` - ${whatsappModalSchedule.end_time}`;
                    message += `\n`;
                  }
                  if (whatsappModalSchedule.location) {
                    message += `*Local:* ${whatsappModalSchedule.location}\n`;
                  }
                  if (whatsappModalSchedule.rehearsal_time) {
                    message += `⏰ *Ensaio:* ${whatsappModalSchedule.rehearsal_time}\n`;
                  }
                  
                  if (member.role) {
                    message += `\n"""""""""""""""""""\n`;
                    message += `*SUA FUNÇÃO: ${member.role.toUpperCase()}*\n`;
                    message += `"""""""""""""""""""\n`;
                  }
                  
                  if (whatsappModalSchedule.members?.length > 0) {
                    message += `\n*EQUIPE COMPLETA:*\n`;
                    whatsappModalSchedule.members.forEach(m => {
                      message += `• ${m.name}`;
                      if (m.role) message += ` → *${m.role}*`;
                      message += `\n`;
                    });
                  }
                  
	                  if (whatsappModalSchedule.songs?.length > 0) {
	                    message += `\n*HINOS DA ESCALA:*\n`;
	                    whatsappModalSchedule.songs.forEach((s, i) => {
	                      message += `${i+1}. *${s.title}*`;
	                      if (s.key) message += ` (Tom: ${s.key})`;
	                      message += `\n`;
	                    });
	                  }
                  
                  if (whatsappModalSchedule.observations) {
                    message += `\n"""""""""""""""""""\n`;
                    message += `*OBSERVAÇÕES:*\n${whatsappModalSchedule.observations}\n`;
                  }
                  
                  message += `\n\n*Confirmar Presença ou Solicitar Alteração em Escala*`;
                  message += `\n*Deus abençoe nosso louvor!*`;
                  
                  const cleanPhone = phone?.replace(/\D/g, '');
                  const whatsappUrl = cleanPhone ? `https://wa.me/55${cleanPhone}?text=${encodeURIComponent(message)}` : null;

                  return (
                    <div key={idx} className="p-4 bg-white rounded-lg border-2 border-gray-200 hover:border-green-300 transition-all">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          {scheduleMemberPhotoById.get(member.id) ? (
                            <img
                              src={scheduleMemberPhotoById.get(member.id)}
                              alt={member.name}
                              className="w-10 h-10 rounded-full object-cover border border-gray-200 flex-shrink-0"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 border border-indigo-200 flex items-center justify-center font-bold text-sm flex-shrink-0">
                              {member.name?.charAt(0)?.toUpperCase() || 'I'}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                          <p className="font-bold text-gray-900">{member.name}</p>
                          {member.role && (
                            <div className="mt-1">
                              <span className="text-xs font-semibold text-white bg-indigo-600 px-2 py-1 rounded">
                                {member.role}
                              </span>
                            </div>
                          )}
                        </div>
                          </div>
                        {whatsappUrl ? (
                          <a
                            href={whatsappUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Button size="sm" className="bg-green-600 hover:bg-green-700">
                              <MessageCircle className="w-4 h-4 mr-2" />
                              Enviar
                            </Button>
                          </a>
                        ) : (
                          <Button size="sm" variant="outline" disabled>
                            Sem telefone
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => setWhatsappModalSchedule(null)}>
                  Fechar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={(open) => { if (open) setShowForm(true); else resetForm(); }}>
        <DialogContent ref={formDialogRef} className="mbv-schedule-editor max-w-[95vw] sm:max-w-4xl max-h-[90vh] overflow-auto p-4 sm:p-7">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">{editingSchedule ? 'Editar Escala' : 'Nova Escala'}</DialogTitle>
          </DialogHeader>

          <div className="mbv-schedule-form mt-2 sm:mt-4">
            <p className="mbv-schedule-intro">Monte a escala em etapas. Escolha primeiro os integrantes, atribua as funções e revise antes de salvar.</p>
            <ScheduleFormProgress steps={FORM_STEPS} activeStep={activeFormStep} onStepChange={goToFormStep} formData={formData} />
            <section className="mbv-schedule-stage" hidden={activeFormStep !== 1}>
            <div className="mbv-schedule-step"><span>01</span><div><strong>Dados da escala</strong><small>Defina data, local e horários</small></div></div>
            <div>
              <Label>Título *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ex: Culto Dominical"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label className="text-sm">Data *</Label>
                <Input
                  type="date"
                  lang="pt-BR"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="text-sm"
                />
              </div>
              <div>
                <Label className="text-sm">Local</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Ex: Igreja"
                  className="text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <div>
                <Label className="text-sm">Início *</Label>
                <Input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  className="text-sm"
                />
              </div>
              <div>
                <Label className="text-sm">Término *</Label>
                <Input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  className="text-sm"
                />
              </div>
              <div>
                <Label className="text-sm">Ensaio *</Label>
                <Input
                  type="time"
                  value={formData.rehearsal_time}
                  onChange={(e) => setFormData({ ...formData, rehearsal_time: e.target.value })}
                  className="text-sm"
                />
              </div>
            </div>

            </section>
            <section className="mbv-schedule-stage" hidden={activeFormStep !== 2}>
            <div className="mbv-schedule-step"><span>02</span><div><strong>Integrantes e funções</strong><small>{formData.members.length} integrante(s) selecionado(s) · escolha uma função para cada pessoa</small></div></div>
            <div className="mbv-schedule-panel">
              <Label className="mb-3 block">Integrantes</Label>
              <div className="mbv-schedule-member-list max-h-96 overflow-y-auto space-y-2 border rounded-lg p-3">
                {sortedTeamMembers.map(member => {
                  const block = formData.date ? getAvailabilityBlockForMember(member, formData.date) : null;
                  const isBlocked = !!block;
                  const canSelectMembers = hasRequiredScheduleTimes(formData, 'rehearsal_time');
                  const isSelected = formData.members.some(m => m.id === member.id);
                  const selectedMember = formData.members.find(m => m.id === member.id);
                  const memberConflict = canSelectMembers && formData.date
                    ? findMemberScheduleConflict({
                        id: member.id,
                        name: member.full_name,
                        member_id: member.member_id || null,
                        member_email: member.email || ''
                      })
                    : null;
                  const isConflicting = !!memberConflict;
                  const unavailableClass = isBlocked
                    ? 'bg-red-50 border border-red-200 rounded-lg p-3'
                    : isConflicting
                      ? 'bg-yellow-50 border border-yellow-200 rounded-lg p-3'
                      : '';
                  const selectedClass = isBlocked
                    ? 'bg-red-50 border border-red-200 rounded-lg p-3'
                    : isConflicting
                      ? 'bg-yellow-50 border border-yellow-200 rounded-lg p-3'
                      : 'bg-indigo-50 border border-indigo-200 rounded-lg p-3';
                  
                  return (
                    <div 
                      key={member.id} 
                      data-selected={isSelected}
                      className={`${isBlocked || isConflicting || !canSelectMembers ? 'opacity-75' : ''} ${isSelected ? selectedClass : unavailableClass}`}
                    >
                      <div 
                        className={`flex items-center gap-2`}
                        title={
                          !canSelectMembers
                            ? REQUIRED_SCHEDULE_TIME_MESSAGE
                            : isBlocked
                              ? UNAVAILABLE_MEMBER_MESSAGE
                              : isConflicting
                                ? `Escalado em ${memberConflict.ministry} (${memberConflict.scheduleTime})`
                                : ''
                        }
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleMember(member.id)}
                          disabled={!isSelected && (!canSelectMembers || isBlocked || isConflicting)}
                        />
                        {scheduleMemberPhotoById.get(member.id) ? (
                          <img
                            src={scheduleMemberPhotoById.get(member.id)}
                            alt={member.full_name}
                            className="w-8 h-8 rounded-full object-cover border border-gray-200 flex-shrink-0"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 border border-indigo-200 flex items-center justify-center text-xs font-bold flex-shrink-0">
                            {member.full_name?.charAt(0)?.toUpperCase() || 'I'}
                          </div>
                        )}
                        <label 
                          className={`text-sm flex-1 ${isBlocked ? 'cursor-not-allowed line-through text-red-700' : isConflicting ? 'cursor-not-allowed text-yellow-800' : !canSelectMembers && !isSelected ? 'cursor-not-allowed text-gray-400' : 'cursor-pointer'}`}
                          onClick={() => toggleMember(member.id)}
                        >
                          {member.full_name}
                          {isBlocked && <span className="ml-2 text-xs text-red-600">Indisponível</span>}
                          {isConflicting && <span className="ml-2 text-xs text-yellow-700">Escalado em outra escala</span>}
                        </label>
                      </div>
                      
                      {isSelected && !isBlocked && (
                        <div className="mt-3 ml-6 space-y-2 bg-indigo-50 p-3 rounded-lg border border-indigo-200">
                          <Label className="text-xs text-gray-700 font-semibold">Função (voz ou instrumento) *</Label>
                          <ScheduleRoleField
                            value={selectedMember?.role || ''}
                            onChange={(value) => {
                              const updated = formData.members.map(m =>
                                m.id === member.id ? { ...m, role: value } : m
                              );
                              setFormData({ ...formData, members: updated });
                            }}
                            catalog={roleCatalog}
                          />
                          {!selectedMember?.role && <p className="text-xs text-red-600 font-semibold">Escolha uma função para este integrante.</p>}
                        </div>
                      )}
                    </div>
                  );
                })}
                {members.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-2">Nenhum integrante cadastrado</p>
                )}
              </div>
            </div>

            </section>
            <section className="mbv-schedule-stage" hidden={activeFormStep !== 3}>
            <div className="mbv-schedule-step"><span>03</span><div><strong>Hinos</strong><small>{formData.songs.length} hino(s) selecionado(s)</small></div></div>
            <div className="mbv-schedule-panel">
              <Label className="mb-3 block">Hinos</Label>
              <div className="mbv-schedule-song-list max-h-48 overflow-y-auto space-y-2 border rounded-lg p-3">
                {sortedSongs.map(song => (
                  <div key={song.id} className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.songs.some(s => s.id === song.id)}
                      onCheckedChange={() => toggleSong(song.id)}
                    />
                    <label className="text-sm cursor-pointer flex-1" onClick={() => toggleSong(song.id)}>
                      {song.title} {song.key && <span className="text-gray-500">({song.key})</span>}
                    </label>
                  </div>
                ))}
                {songs.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-2">Nenhum hino cadastrado</p>
                )}
              </div>
            </div>

            </section>
            <section className="mbv-schedule-stage" hidden={activeFormStep !== 4}>
            <div className="mbv-schedule-step"><span>04</span><div><strong>Finalizar</strong><small>Revise os detalhes antes de salvar</small></div></div>
            <ScheduleDraftSummary formData={formData} hasSongs={true} />
            <div>
              <Label>Observações</Label>
              <Textarea
                value={formData.observations}
                onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
                rows={3}
              />
            </div>

            </section>
            <div className="mbv-schedule-wizard-actions">
              <Button type="button" variant="outline" onClick={resetForm}>Cancelar</Button>
              <div>
                {activeFormStep > 1 && <Button type="button" variant="outline" onClick={() => goToFormStep(activeFormStep - 1)}>Voltar</Button>}
                {activeFormStep < FORM_STEPS.length ? (
                  <Button type="button" onClick={() => goToFormStep(activeFormStep + 1)}>Próximo passo</Button>
                ) : (
                  <Button type="button" onClick={handleSubmit} disabled={createMutation.isPending || updateMutation.isPending}>
                    {createMutation.isPending || updateMutation.isPending ? 'Salvando escala...' : editingSchedule ? 'Salvar alterações' : 'Criar escala'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
